<template>
    <div class="settingsEmail">
        <Card>
            <Heading
                :labelTitle="$t.__('email.SettingsEmail.title')"
                variant="h2"
                :prefixColoredIcon="{ icon: 'mail-opened', color: 'brand' }"
            />
            <template v-if="!isLoading">
                <EmailSettingAccountNotSynced
                    v-if="!isSetup"
                    v-bind="account"
                    :isEmailsSentFromCorp="isEmailsSentFromCorp"
                />
                <EmailSettingAccountSynced
                    v-else-if="isSyncMode"
                    v-bind="account"
                    :isEmailsSentFromCorp="isEmailsSentFromCorp"
                    @updateEmailStatus="getSettings"
                    @handleDisplayDialog="showDisconnectPopup = true"
                />
            </template>
            <template v-else>
                <CompassSkeleton
                    class="settingsEmail__skeleton"
                    :size="CompassEnums.SkeletonSize.EXTRA_LARGE"
                />
            </template>
            <DisconnectPopup
                v-if="showDisconnectPopup"
                :visible="showDisconnectPopup"
                @close="showDisconnectPopup = false"
                @handleDisconnect="logout"
            />
        </Card>
        <EmailSettingPreferences
            v-if="isLoading || isSetup"
            :privateEmail="privateEmail"
            :automaticallyAttachDocumentValue="automaticallyAttachDocumentValue"
            :ccEmails="ccEmails"
            :isLoading="isLoading"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted } from 'vue';
import { CompassEnums } from '@sellsy/compass2';
import type { Translator } from 'types/injects/translator.type';
import type { PushToast } from 'types/injects/pushToast.type';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import { getEmailSettings } from 'api/emailEngine/emailEngineService';
import EmailSettingAccountSynced from 'vcomponents/email/EmailSettingAccountSynced.vue';
import EmailSettingAccountNotSynced from 'vcomponents/email/EmailSettingAccountNotSynced.vue';
import DisconnectPopup from 'vcomponents/email/DisconnectPopup.vue';
import EmailSettingPreferences from 'vcomponents/email/emailSettings/EmailSettingPreferences.vue';

declare const SellsyJS: { WebServiceCaller: { post: Function } };

interface AccountData {
    isSetup: 'Y' | 'N';
    email?: string;
    modeName?: string;
    status?: string;
    isNylas?: 'Y' | 'N';
}

interface EmailPrefsData {
    emailsSentFromCorp: 'Y' | 'N';
    onboarding_setup?: 'Y' | 'N';
}

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const account = ref<AccountData>({ isSetup: 'N' });
const emailPrefs = ref<EmailPrefsData>({ emailsSentFromCorp: 'N' });
const isLoading = ref(true);
const emailSuccessfullyLinked = ref(false);
const showDisconnectPopup = ref(false);
const privateEmail = ref(false);
const automaticallyAttachDocumentValue = ref(true);
const ccEmails = ref<string[]>([]);

const isSetup = computed(() => account.value.isSetup === 'Y');
const isEmailsSentFromCorp = computed(() => emailPrefs.value.emailsSentFromCorp === 'Y');
const isSyncMode = computed(() => account.value.isNylas === 'Y');

const getAccountData = (connection: Record<string, string> | null): AccountData => {
    if (connection === null) {
        return { isSetup: 'N' };
    }
    return {
        isSetup: 'Y',
        email: connection.email,
        modeName:
            connection.type === 'synchronization'
                ? $t.__('email.SettingsEmail.syncMode')
                : $t.__('email.SettingsEmail.transferMode'),
        status: connection.status,
        isNylas: connection.type === 'synchronization' ? 'Y' : 'N',
    };
};

const getEmailPrefsData = (connection: Record<string, string> | null): EmailPrefsData => {
    if (connection === null) {
        return { emailsSentFromCorp: 'N' };
    }
    return {
        emailsSentFromCorp: connection.emails_sent_from_corp ? 'Y' : 'N',
        onboarding_setup: connection.onboarding_setup ? 'Y' : 'N',
    };
};

const getSettings = () => {
    isLoading.value = true;
    getEmailSettings()
        .then((response: { data: Record<string, unknown> }) => {
            const connection = response.data.connection as Record<string, string> | null;
            account.value = getAccountData(connection);
            emailPrefs.value = getEmailPrefsData(connection);
            isLoading.value = false;
            privateEmail.value = response.data.private_mode as boolean;
            automaticallyAttachDocumentValue.value = response.data
                .automatically_attach_document as boolean;
            ccEmails.value = (response.data.cc_emails as string[]) || [];

            if (emailSuccessfullyLinked.value && connection) {
                $pushToast({
                    type: CompassEnums.ToastType.SUCCESS,
                    close: true,
                    icon: true,
                    text: $t.__('settings.SettingsEmail.email_correctly_configured'),
                });
            }
        })
        .catch((error: string) => {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: error,
            });
        });
};

const logout = () => {
    isLoading.value = true;
    SellsyJS.WebServiceCaller.post('?_f=prefsNylas&action=logout', {}, {}, () => {
        getSettings();
        showDisconnectPopup.value = false;
    });
};

onMounted(() => {
    getSettings();
    const params = new URLSearchParams(window.location.search);
    emailSuccessfullyLinked.value = !!params.get('status');
});
</script>

<style lang="scss" scoped>
.settingsEmail {
    display: flex;
    gap: var(--spacing-4);
    align-items: flex-start;

    & > * {
        flex: 1;
    }

    :deep(.card-container + .card-container) {
        margin-top: 0;
    }

    @media (max-width: 1199px) {
        flex-direction: column;

        & > * {
            width: 100%;
        }
    }

    &__skeleton {
        max-width: 400px;
    }
}
</style>
